<template>
  <div v-if="imageCount > 0 || isLoading" id="lookbook" class="py-14 md:py-24">
    <div class="mb-8 w-full md:mb-12">
      <h2 class="text-[0.62rem] font-normal uppercase tracking-[0.36em] text-[#1a1714]/60">{{ $t('common.lookbook') }}</h2>
    </div>

    <!-- Loading indicator -->
    <div v-if="isLoading" class="flex justify-center items-center py-16">
      <div class="text-center">
        <div class="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-white bg-black">
          <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ $t('messages.loadingLookbook') }}
        </div>
      </div>
    </div>
    
    <!-- Lookbook content -->
    <div v-else class="w-full">
      <div
        class="grid w-full gap-3 md:gap-4"
        :class="gridClasses"
      >
        <div 
          v-for="item in lookbookItems"
          :key="item.index"
          class="group relative w-full cursor-pointer overflow-hidden bg-[#e4d8c8]"
          :style="{ aspectRatio: item.type === 'video' ? '9 / 16' : '2 / 3' }"
          @click="openModal(item.displayPath || item.path, item.type, item.zoomPath || item.path)"
        >
          <!-- Video -->
          <video
            v-if="item.type === 'video'"
            :src="item.path"
            class="lookbook-media w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            autoplay
            muted
            loop
            playsinline
            controls
            preload="metadata"
            crossorigin="anonymous"
            @error="handleMediaError"
            @loadstart="handleVideoLoadStart"
            @canplay="handleVideoCanPlay"
          />
          <!-- Image -->
          <KImage
            v-else
            :src="item.displayPath || item.path"
            :alt="`${productDisplayName} Kendrick Paris lookbook image ${item.index}`"
            sizes="100vw md:50vw lg:480px"
            width="1400"
            :quality="92"
            class="lookbook-media block h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            @error="handleMediaError"
            @load="handleImageLoad"
          />
          <span
            v-if="item.type !== 'video'"
            class="pointer-events-none absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-black opacity-0 shadow-md transition-opacity duration-200 group-hover:opacity-100"
          >
            <ZoomIn class="h-4 w-4" :stroke-width="1.5" aria-hidden="true" />
          </span>
          <!-- Shoppable overlay -->
          <div class="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            <p class="text-white text-sm font-light tracking-wide">{{ productDisplayName }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Lookbook Modal -->
    <LookbookModal
      :show="showLookbookModal"
      :modal-image="selectedLookbookImage"
      :modal-zoom-image="selectedLookbookZoomImage"
      :modal-video="selectedLookbookVideo"
      :alt-text="`${productDisplayName} Kendrick Paris lookbook`"
      :product-type="productType"
      :max-image-count="imageCount"
      @close="closeModal"
      @update-image="updateModalImage"
      @navigate-lookbook="navigateLookbook"
    />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref, onMounted, computed, watch } from 'vue'
import { ZoomIn } from 'lucide-vue-next'

interface Props {
  productType?: 'pumps' | 'boots' | 'sandals' | 'ankle-boots'
  color?: string
  apiPhotos?: string[]
  apiPhotosZoom?: string[]
}

interface LookbookItem {
  index: number
  path: string
  displayPath?: string
  zoomPath?: string
  type: 'image' | 'video'
}

function normalizeLookbookMediaKey(path: string) {
  const fileName = path.split('/').pop()?.split('?')[0] || path
  return fileName.replace(/z(?=\.[a-z0-9]+$)/i, '').toLowerCase()
}

function resolveZoomPath(photoPath: string, index: number, zoomPaths: string[]) {
  const indexedZoomPath = zoomPaths[index] || ''
  if (indexedZoomPath && normalizeLookbookMediaKey(indexedZoomPath) === normalizeLookbookMediaKey(photoPath)) {
    return indexedZoomPath
  }

  return zoomPaths.find(zoomPath => normalizeLookbookMediaKey(zoomPath) === normalizeLookbookMediaKey(photoPath)) || ''
}

function isDerivativePath(path?: string) {
  return Boolean(path && path.includes('/derivatives/'))
}

function scoreLookbookItem(item: LookbookItem) {
  let score = 0

  if (item.zoomPath) score += 40
  if (!isDerivativePath(item.path)) score += 20
  if (item.zoomPath && !isDerivativePath(item.zoomPath)) score += 20

  return score
}

function prioritizeLookbookItems(items: LookbookItem[]) {
  return [...items].sort((left, right) => {
    const scoreDiff = scoreLookbookItem(right) - scoreLookbookItem(left)
    if (scoreDiff !== 0) return scoreDiff
    return left.index - right.index
  })
}

const MAX_LOOKBOOK_ITEMS = 6
const MIN_LOOKBOOK_ITEMS: Record<string, number> = {
  'ankle-boots:black': 3
}
const LOOKBOOK_CURATIONS: Record<string, number[]> = {
  'boots:black': [13, 7, 10, 19, 21, 1],
  'ankle-boots:black': [1, 7, 10, 14, 21, 24]
}
const LOOKBOOK_FALLBACK_ITEMS: Record<string, LookbookItem[]> = {
  'ankle-boots:black': [
    {
      index: 21,
      path: '/product/ankle-boots/looks/kendrick-ankle-boots-black-21.jpg',
      zoomPath: '',
      type: 'image'
    },
    {
      index: 24,
      path: '/product/ankle-boots/looks/kendrick-ankle-boots-black-24.jpg',
      zoomPath: '/product/ankle-boots/looks/kendrick-ankle-boots-black-24z.jpg',
      type: 'image'
    }
  ]
}

// Exact looks (path fragments, in order) for lookbooks that must show a fixed set of three.
const LOOKBOOK_EXACT_SET: Record<string, string[]> = {
  'ankle-boots:black': ['ankle-boots-black-bed.', 'ankle-boots-black-21.', 'ankle-boots-black-19.']
}

// Looks that are not in the product feed: full-resolution portrait crops made from editorial shots.
const LOOKBOOK_EXTRA_ITEMS: Record<string, LookbookItem> = {
  'ankle-boots-black-bed.': {
    index: 900,
    path: '/product/ankle-boots/looks/kendrick-ankle-boots-black-bed.jpg',
    zoomPath: '/product/ankle-boots/looks/kendrick-ankle-boots-black-bed.jpg',
    type: 'image'
  }
}

function withLookbookExtras(items: LookbookItem[]) {
  const exactSet = LOOKBOOK_EXACT_SET[buildLookbookKey()]
  if (!exactSet) return items

  const picked = exactSet
    .map(fragment => items.find(item => item.path.includes(fragment)) || LOOKBOOK_EXTRA_ITEMS[fragment])
    .filter((item): item is LookbookItem => Boolean(item))
  return picked.length === exactSet.length ? picked : items.slice(0, exactSet.length)
}

const props = withDefaults(defineProps<Props>(), {
  productType: 'pumps',
  color: undefined,
  apiPhotos: () => [],
  apiPhotosZoom: () => []
})

const { t } = useI18n()

const productNames: Record<string, string> = {
  'boots': 'AXIS 110 Boot',
  'ankle-boots': 'ALIGN 110 Ankle Boot',
  'pumps': 'EDGE 110 Pump',
  'sandals': 'POISE 110 Sandal'
}

const productDisplayName = computed(() => {
  const name = productNames[props.productType] || props.productType
  const colorLabel = props.color ? t(`colors.${props.color}`, props.color) : ''
  return colorLabel ? `${colorLabel} ${name}` : name
})

const showLookbookModal = ref(false)
const selectedLookbookImage = ref('')
const selectedLookbookZoomImage = ref('')
const selectedLookbookVideo = ref('')
const lookbookItems = ref<LookbookItem[]>([])
const imageCount = ref(0)
const isLoading = ref(false)
let lookbookLoadRequest = 0

function buildLookbookKey() {
  return `${props.productType}:${String(props.color || '').toLowerCase()}`
}

function ensureMinimumLookbookItems(items: LookbookItem[]) {
  const key = buildLookbookKey()
  const minimumCount = MIN_LOOKBOOK_ITEMS[key] || 0
  const fallbackItems = LOOKBOOK_FALLBACK_ITEMS[key] || []
  if (!minimumCount || items.length >= minimumCount || fallbackItems.length === 0) return items

  const usedKeys = new Set(
    items.flatMap(item => [item.path, item.displayPath, item.zoomPath])
      .filter((path): path is string => Boolean(path))
      .map(path => normalizeLookbookMediaKey(path))
  )
  const nextItems = [...items]

  for (const fallbackItem of fallbackItems) {
    if (nextItems.length >= minimumCount) break

    const fallbackKeys = [fallbackItem.path, fallbackItem.displayPath, fallbackItem.zoomPath]
      .filter((path): path is string => Boolean(path))
      .map(path => normalizeLookbookMediaKey(path))

    if (fallbackKeys.some(pathKey => usedKeys.has(pathKey))) continue

    nextItems.push(fallbackItem)
    fallbackKeys.forEach(pathKey => usedKeys.add(pathKey))
  }

  return nextItems
}

function selectSpacedItems(items: LookbookItem[], limit: number) {
  if (items.length <= limit) return items

  const pickedIndexes = new Set<number>()
  const lastIndex = items.length - 1

  for (let step = 0; step < limit; step++) {
    const index = Math.round((step * lastIndex) / Math.max(limit - 1, 1))
    pickedIndexes.add(index)
  }

  if (pickedIndexes.size < limit) {
    for (let index = 0; index <= lastIndex && pickedIndexes.size < limit; index++) {
      pickedIndexes.add(index)
    }
  }

  return Array.from(pickedIndexes)
    .sort((left, right) => left - right)
    .slice(0, limit)
    .map(index => items[index])
}

function curateLookbookItems(items: LookbookItem[]) {
  if (items.length <= MAX_LOOKBOOK_ITEMS) return items

  const manualOrder = LOOKBOOK_CURATIONS[buildLookbookKey()]
  if (manualOrder?.length) {
    const itemsByIndex = new Map(items.map(item => [item.index, item]))
    const curatedItems = manualOrder
      .map(index => itemsByIndex.get(index))
      .filter((item): item is LookbookItem => Boolean(item))

    if (curatedItems.length >= MAX_LOOKBOOK_ITEMS) {
      return curatedItems.slice(0, MAX_LOOKBOOK_ITEMS)
    }

    const usedIndexes = new Set(curatedItems.map(item => item.index))
    const remainingItems = items.filter(item => !usedIndexes.has(item.index))
    return [...curatedItems, ...selectSpacedItems(remainingItems, MAX_LOOKBOOK_ITEMS - curatedItems.length)]
  }

  return selectSpacedItems(items, MAX_LOOKBOOK_ITEMS)
}

// Every look shares one portrait frame: landscape and portrait are never mixed.
// 1 look stays narrow, 2 and 4 go two-up, everything else three-up.
const gridClasses = computed(() => {
  const count = imageCount.value
  if (count === 1) return 'grid-cols-1 max-w-[30rem] mx-auto'
  if (count === 2 || count === 4) return 'grid-cols-1 sm:grid-cols-2'
  return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'
})

const openModal = (path: string, type: 'image' | 'video', zoomPath?: string) => {
  if (type === 'video') {
    selectedLookbookVideo.value = path
    selectedLookbookImage.value = ''
    selectedLookbookZoomImage.value = ''
  } else {
    selectedLookbookImage.value = path
    selectedLookbookZoomImage.value = zoomPath || ''
    selectedLookbookVideo.value = ''
  }
  
  // Track lookbook click
  const { trackLookbookClick } = useGA()
  const imageIndex = lookbookItems.value.findIndex(item => item.path === path)
  trackLookbookClick(props.productType, props.color || 'all', imageIndex >= 0 ? imageIndex : 0)
  
  showLookbookModal.value = true
}

const closeModal = () => {
  showLookbookModal.value = false
  selectedLookbookImage.value = ''
  selectedLookbookZoomImage.value = ''
  selectedLookbookVideo.value = ''
}

const updateModalImage = (payload: { image: string, video: string, zoomImage?: string }) => {
  selectedLookbookImage.value = payload.image
  selectedLookbookZoomImage.value = payload.zoomImage || ''
  selectedLookbookVideo.value = payload.video
}

const navigateLookbook = (direction: 'prev' | 'next') => {
  if (lookbookItems.value.length === 0) return
  
  const currentPath = selectedLookbookVideo.value || selectedLookbookImage.value
  const currentIndex = lookbookItems.value.findIndex(item => item.path === currentPath || item.zoomPath === currentPath)
  
  if (currentIndex === -1) return
  
  let newIndex = currentIndex
  if (direction === 'prev') {
    newIndex = currentIndex <= 0 ? lookbookItems.value.length - 1 : currentIndex - 1
  } else {
    newIndex = currentIndex >= lookbookItems.value.length - 1 ? 0 : currentIndex + 1
  }
  
  const newItem = lookbookItems.value[newIndex]
  if (newItem.type === 'video') {
    selectedLookbookVideo.value = newItem.path
    selectedLookbookImage.value = ''
    selectedLookbookZoomImage.value = ''
  } else {
    const displayPath = newItem.displayPath || newItem.path
    selectedLookbookImage.value = displayPath
    selectedLookbookZoomImage.value = newItem.zoomPath || displayPath
    selectedLookbookVideo.value = ''
  }
}

const handleMediaError = (event: Event | string) => {
  // If media fails to load, log the error but don't remove from list
  if (typeof event === 'string') return
  
  const target = event.target as HTMLImageElement | HTMLVideoElement
  if (!target) return
  
  const src = target.src || (target as HTMLVideoElement).src
  if (!src) return
  
  console.warn('LookbookGrid: Media failed to load:', src)
  
  // Don't remove from list - just log the error
  // The image will show as broken but won't break the layout
}

const handleVideoLoadStart = (event: Event) => {

}

const handleVideoCanPlay = (event: Event) => {

}

const handleImageLoad = (event: Event) => {
  // Image loaded successfully
}

// Removed checkMediaExists function to prevent route warnings

const countImages = async () => {
  if (!import.meta.client) return

  const requestId = ++lookbookLoadRequest

  if (!props.color) {
    lookbookItems.value = []
    imageCount.value = 0
    isLoading.value = false
    return
  }

  const productType = props.productType || 'pumps'
  const color = props.color

  // Loading images for product
  isLoading.value = true

  if (props.apiPhotos.length > 0) {
    const zoomPaths = Array.isArray(props.apiPhotosZoom) ? props.apiPhotosZoom.filter(Boolean) : []
    const apiItems: LookbookItem[] = props.apiPhotos.map((path, index) => {
      const zoomPath = resolveZoomPath(path, index, zoomPaths)

      return {
        index: index + 1,
        path,
        zoomPath,
        displayPath: path,
        type: 'image'
      }
    })

    const curatedApiItems = withLookbookExtras(ensureMinimumLookbookItems(curateLookbookItems(prioritizeLookbookItems(apiItems))))
    if (requestId !== lookbookLoadRequest) return
    lookbookItems.value = curatedApiItems
    imageCount.value = curatedApiItems.length
    isLoading.value = false
    return
  }

  // Try to load images and videos dynamically by checking if they exist
  const validItems: LookbookItem[] = []
  let count = 0
  let hasMore = true
  let consecutiveFailures = 0
  const maxConsecutiveFailures = 3 // Stop after 3 consecutive failures
  
  while (hasMore && count < 50 && consecutiveFailures < maxConsecutiveFailures) {
    count++
    
    // Check for both JPG and MP4 files
    const imagePath = `/product/${productType}/looks/kendrick-${productType}-${color}-${count}.jpg`
    const videoPath = `/product/${productType}/looks/kendrick-${productType}-${color}-${count}.mp4`
    
    let found = false
    
    // First try to load the image - suppress router warnings by catching errors silently
    try {
      const img = new Image()
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Timeout')), 3000)
        img.onload = () => {
          clearTimeout(timeout)
          if (requestId !== lookbookLoadRequest) {
            resolve(false)
            return
          }
          validItems.push({
            index: count,
            path: imagePath,
            zoomPath: '',
            type: 'image' as const
          })
          found = true
          consecutiveFailures = 0
          resolve(true)
        }
        img.onerror = () => {
          clearTimeout(timeout)
          reject(new Error('Image not found'))
        }
        // Use absolute URL to avoid router interception
        const absolutePath = imagePath.startsWith('/') 
          ? `${window.location.origin}${imagePath}`
          : imagePath
        img.src = absolutePath
      })
    } catch {
      // If image fails, try video
      try {
        const video = document.createElement('video')
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Timeout')), 3000)
          video.onloadedmetadata = () => {
            clearTimeout(timeout)
            if (requestId !== lookbookLoadRequest) {
              resolve(false)
              return
            }
            validItems.push({ 
              index: count, 
              path: videoPath, 
              type: 'video' as const 
            })
            found = true
            consecutiveFailures = 0
            resolve(true)
          }
          video.onerror = () => {
            clearTimeout(timeout)
            reject(new Error('Video not found'))
          }
          // Use absolute URL to avoid router interception
          const absolutePath = videoPath.startsWith('/') 
            ? `${window.location.origin}${videoPath}`
            : videoPath
          video.src = absolutePath
        })
      } catch {
        // If both image and video fail, increment failure counter
        consecutiveFailures++
        if (consecutiveFailures >= maxConsecutiveFailures) {
          hasMore = false
        }
      }
    }
  }
  
  const curatedItems = withLookbookExtras(ensureMinimumLookbookItems(curateLookbookItems(validItems)))
  if (requestId !== lookbookLoadRequest) return
  lookbookItems.value = curatedItems
  imageCount.value = curatedItems.length
  isLoading.value = false
  
  // Images loaded successfully
}

// Add GTM tracking for lookbook view
const { trackLookbookView } = useGA()

watch(
  () => [props.productType, props.color, props.apiPhotos, props.apiPhotosZoom],
  () => {
    void countImages()
  },
  { immediate: true, deep: true }
)

onMounted(() => {
  trackLookbookView(props.productType, props.color || 'all')
})
</script> 

<style scoped>
.lookbook-media {
  filter: contrast(0.985) saturate(0.95) brightness(0.995);
}
</style>
